/** Rules: all 18 adaptation rules in full, plus the start-date setting. Section 7.
 *  "Display them in full; do not summarise." */
import { useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner } from './components.tsx';
import { PLAN, META } from '../plan/data.ts';
import { isMonday, parseIsoLocal, DateError } from '../plan/dates.ts';
import { THEMES, isTheme } from './theme.ts';
import type { Theme } from './theme.ts';

export function Rules({ api }: { api: StoreApi }) {
  const { state, durable } = api;
  const stored = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const [draft, setDraft] = useState(stored);
  const [note, setNote] = useState<string | null>(null);
  const key = 'setting:startDate';
  const pending = api.pending.has(key);
  const err = api.errorFor(key);

  let valid = true, warn: string | null = null;
  try {
    parseIsoLocal(draft);
    if (!isMonday(draft)) warn = 'That is not a Monday. The plan is built around Monday starts, '
      + 'but you can use it anyway — every day-of-week mapping will shift with it.';
  } catch (e) {
    valid = false;
    warn = e instanceof DateError ? e.message : String(e);
  }

  const save = async () => {
    setNote(null);
    if (!valid) return;
    const ok = await api.put(key, draft);
    setNote(ok
      ? `Saved. Start date is ${draft}.`
      : 'Not saved. The write did not return successfully.');
  };

  return (
    <>
      <h1>Rules</h1>

      <h2>Display</h2>
      <div className="card">
        <span className="label" id="theme-label">Theme</span>
        <div className="segbar" role="group" aria-labelledby="theme-label">
          {THEMES.map((t) => {
            const stored = state.get<unknown>('setting:theme');
            const current: Theme = isTheme(stored) ? stored : 'system';
            return (
              <button key={t} aria-current={current === t ? 'page' : undefined}
                      disabled={!durable || api.pending.has('setting:theme')}
                      onClick={() => void api.put('setting:theme', t)}>
                {t === 'system' ? 'System' : t === 'light' ? 'Light' : 'Dark'}
              </button>
            );
          })}
        </div>
        <p className="sub" style={{ marginBottom: 0 }}>
          System follows your phone. The override is stored like any other setting, so it
          is backed up and mirrored with everything else.
        </p>
        {api.errorFor('setting:theme') && (
          <Banner kind="bad" title="Not saved">{api.errorFor('setting:theme')}</Banner>
        )}
      </div>

      <h2>Start date</h2>
      <div className="card">
        <p className="sub" style={{ marginTop: 0 }}>
          Everything derives from this. Changing it re-dates every day of the plan; your
          ticks stay attached to their day numbers, not to calendar dates.
        </p>
        <label className="label" htmlFor="sd">Start date (a Monday)</label>
        <input id="sd" type="date" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <div className="btns">
          <button className="primary" disabled={!durable || pending || !valid || draft === stored}
                  onClick={() => void save()}>
            {pending ? 'Saving…' : 'Save start date'}
          </button>
          {draft !== stored && <button onClick={() => { setDraft(stored); setNote(null); }}>Cancel</button>}
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <span className="k">Currently stored</span>
          <span className="v">{stored}{state.has(key) ? '' : ' (default, never saved)'}</span>
        </div>
        {warn && <Banner kind={valid ? 'warn' : 'bad'}>{warn}</Banner>}
        {err && <Banner kind="bad" title="Not saved">{err}</Banner>}
        {note && <Banner kind={note.startsWith('Saved') ? 'ok' : 'bad'}>{note}</Banner>}
      </div>

      <h2>Adaptation rules</h2>
      <p className="sub">
        All {PLAN.rules.length}, in full. These are load-bearing — particularly the
        calorie-steering rule, which tells you to override the plan's own arithmetic with
        your own data from week 3.
      </p>
      {PLAN.rules.map(([title, body], i) => (
        <div className="card" key={i}>
          <div className="rule-title">{i + 1}. {title}</div>
          {/* The rules are written in paragraphs; rendering them as one block would
              collapse the structure that makes them readable. */}
          {body.split(/\n\n+/).map((para, j) => (
            <p className="rulebody" key={j}>{para}</p>
          ))}
        </div>
      ))}
    </>
  );
}
